import { Component, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { filter, startWith } from 'rxjs/operators';
import { selectVacancyApplicationIsLoading, selectCvBoxApplicationIsLoading } from 'src/app/root-store/jobseeker-store/jobseeker-applies/selectors';
import { AppState } from 'src/app/root-store/state';
import { selectCurrentWebsiteNameForSeo } from 'src/app/root-store/selectors';
import { MetaService } from 'src/app/shared/services/meta.service';

@Component({
    selector: 'app-jobseeker-applies',
    templateUrl: './jobseeker-applies.component.html',
    styleUrls: ['./jobseeker-applies.component.scss'],
    standalone: false
})
export class JobseekerAppliesComponent implements OnInit, OnDestroy {

  public readonly vacanciesTab = 0;
  public readonly invitationsTab = 1;
  public readonly cvBoxesTab = 2;

  public activeTab = this.vacanciesTab;

  public metaDataSubscription: Subscription;

  public cvBoxApplicationIsLoading$: Observable<boolean>;
  public vacancyApplicationIsLoading$: Observable<boolean>;

  private routerSub: Subscription;

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private metaService: MetaService,
  ) { }

  ngOnInit(): void {
    this.cvBoxApplicationIsLoading$ = this.store.pipe(select(selectCvBoxApplicationIsLoading));
    this.vacancyApplicationIsLoading$ = this.store.pipe(select(selectVacancyApplicationIsLoading));

    this.routerSub = this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        startWith(null)
      )
      .subscribe(() => this.syncTabWithRoute());

      
    this.metaDataSubscription = this.store.pipe(select(selectCurrentWebsiteNameForSeo)).subscribe(d => {
      this.metaService.updateMetaData(d);
    });
  }

  ngOnDestroy(): void {
    this.metaDataSubscription.unsubscribe()
    this.routerSub?.unsubscribe();
  }

  onTabChange(value: number | string): void {
    const tab = Number(value);

    // Reflect the click immediately so the one-way [value] binding stays in sync.
    this.activeTab = tab;

    if (tab === this.invitationsTab) {
      this.router.navigate(['/jobseeker/applies/invitations']);
      return;
    }

    const queryParams = tab === this.cvBoxesTab ? { tab: 'cv-boxes' } : {};

    if (this.isInvitationsRoute()) {
      this.router.navigate(['/jobseeker/applies'], { queryParams });
      return;
    }

    this.location.replaceState(this.router.createUrlTree(['/jobseeker/applies'], { queryParams }).toString());
  }

  private syncTabWithRoute(): void {
    if (this.isInvitationsRoute()) {
      this.activeTab = this.invitationsTab;
    } else if (this.route.snapshot.queryParamMap.get('tab') === 'cv-boxes') {
      this.activeTab = this.cvBoxesTab;
    } else if (this.activeTab === this.invitationsTab) {
      this.activeTab = this.vacanciesTab;
    }
  }

  private isInvitationsRoute(): boolean {
    return this.router.url.includes('/applies/invitations');
  }
}
